// Win Your Wedding — pages for people who don't log in:
//   ?wyw_enter=1        entry form (website / flyer QR)
//   ?wyw_book=TOKEN     a couple books or changes their show-around
//   ?wyw_tonight=TOKEN  a host's show-around list with Done / Undo
import React, { useCallback, useEffect, useState } from 'react';
import { callWyw, errorText, fmtTime, fmtSlot, WYW_GREEN, WYW_GOLD } from '../lib/wywService';

function Shell({ kicker = 'WIN YOUR WEDDING', title, sub, children, bare = false, hideTitle = false }) {
  // Embedded on errigalhotel.com: the page around it carries the branding, so no
  // green header and a transparent background (and no full-screen height, which
  // would stop the iframe from sizing to its content).
  if (bare) {
    return (
      <div className="text-[#2B2B2B]" style={{ fontFamily: 'Lato, Arial, sans-serif', background: 'transparent' }}>
        <div className="max-w-lg mx-auto px-1 py-2">
          {!hideTitle && title && <h2 className="text-2xl mb-1" style={{ fontFamily: 'Georgia, serif', color: WYW_GREEN }}>{title}</h2>}
          {!hideTitle && sub && <p className="text-base mb-4 text-[#5E5E5E]">{sub}</p>}
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#F5F4F2] text-[#2B2B2B]" style={{ fontFamily: 'Lato, Arial, sans-serif' }}>
      <div className="px-5 pt-7 pb-6" style={{ background: WYW_GREEN }}>
        <div className="max-w-lg mx-auto">
          <div className="text-xs font-bold tracking-[0.2em]" style={{ color: WYW_GOLD }}>{kicker}</div>
          <h1 className="text-2xl text-white mt-1" style={{ fontFamily: 'Georgia, serif' }}>{title}</h1>
          {sub && <p className="text-white/85 text-base mt-1">{sub}</p>}
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 py-5">{children}</div>
    </div>
  );
}

const bigBtn = 'w-full h-14 rounded-xl text-white text-lg font-bold disabled:opacity-50';

// ── Entry form ──
const YEARS = ['2027', '2028', 'Not sure yet'];
const GUESTS = ['Under 75', '75–120', '120–200', '200+'];
const VENUE = ['Still looking', 'Booked elsewhere', 'Booked at Errigal'];
const EVENINGS = [['2027-01-08', 'Fri 8 Jan, 5–9pm'], ['2027-01-31', 'Sun 31 Jan, 2–8pm'], ['2027-02-12', 'Fri 12 Feb, 5–9pm']];

// Where the entry came from: ?src=qr on the printed QR (passed through by the
// hotel-site embed). Anything else counts as the website.
function entrySource() {
  try { return new URLSearchParams(window.location.search).get('src') === 'qr' ? 'qr' : null; } catch (_) { return null; }
}
const EMBEDDED = (() => { try { return window.self !== window.top; } catch (_) { return true; } })();

// Inside the hotel-site iframe: tell the parent page how tall we are.
function useReportHeight() {
  useEffect(() => {
    if (!EMBEDDED) return undefined;
    const post = () => {
      try { window.parent.postMessage({ type: 'errigal-wyw-height', height: document.documentElement.scrollHeight }, '*'); } catch (_) { /* ignore */ }
    };
    post();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(post) : null;
    if (ro) ro.observe(document.body);
    window.addEventListener('resize', post);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', post); };
  }, []);
}

// Meta Pixel (same pixel as the table booking widget). Loaded only on the
// public entry form; fires a Lead when an entry goes through.
const META_PIXEL_ID = '1311926915327192';
function loadPixel() {
  try {
    if (window.fbq) return;
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  } catch (_) { /* tracking must never break the form */ }
}
function trackEntry(src) {
  try {
    if (!window.fbq) return;
    window.fbq('track', 'Lead', { content_name: 'Win Your Wedding' });
    window.fbq('trackCustom', 'WinYourWeddingEntry', { source: src || 'website' });
  } catch (_) { /* ignore */ }
}

export function WywEnterPage() {
  useEffect(() => { loadPixel(); }, []);
  useReportHeight();
  useEffect(() => { document.title = 'Win Your Wedding · Errigal Country House Hotel'; }, []);
  const [f, setF] = useState({ names: '', email: '', phone: '', wedding_year: '', guests: '', venue_status: '', evening: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.names.trim() || !f.email.trim()) { setErr('Please add your names and email.'); return; }
    setBusy(true);
    try {
      const r = await callWyw('enter', { ...f, src: entrySource() });
      trackEntry(entrySource());
      setDone(r);
    } catch (x) { setErr(x.code === 'email_required' ? 'Please check your email address.' : errorText(x)); }
    setBusy(false);
  };

  if (done) {
    return (
      <Shell bare={EMBEDDED} title="You're in!" sub="Check your email for your booking link.">
        <p className="text-base mb-4">Next step: book a 15-minute show-around at one of our showcase evenings. You both need to come.</p>
        <a href={done.book_url} target={EMBEDDED ? '_top' : undefined} className={`${bigBtn} flex items-center justify-center`} style={{ background: WYW_GREEN }}>Book your show-around now</a>
      </Shell>
    );
  }

  const input = 'w-full h-12 px-3 rounded-lg border border-[#D6D2C8] bg-white text-base';
  const Select = ({ k, opts, label }) => (
    <label className="block">
      <span className="text-sm font-bold">{label}</span>
      <select value={f[k]} onChange={set(k)} className={`${input} mt-1`}>
        <option value="">Choose…</option>
        {opts.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o}>{o}</option>))}
      </select>
    </label>
  );
  return (
    <Shell bare={EMBEDDED} hideTitle title="Win your wedding" sub="A wedding with everything included for 75 guests · Free to enter">
      <form onSubmit={submit} className="space-y-4">
        <label className="block"><span className="text-sm font-bold">Your names</span>
          <input value={f.names} onChange={set('names')} placeholder="e.g. Aoife & Conor" className={`${input} mt-1`} /></label>
        <label className="block"><span className="text-sm font-bold">Email</span>
          <input type="email" value={f.email} onChange={set('email')} className={`${input} mt-1`} /></label>
        <label className="block"><span className="text-sm font-bold">Mobile</span>
          <input type="tel" value={f.phone} onChange={set('phone')} className={`${input} mt-1`} /></label>
        <Select k="wedding_year" opts={YEARS} label="When is your wedding?" />
        <Select k="guests" opts={GUESTS} label="How many guests?" />
        <Select k="venue_status" opts={VENUE} label="Have you booked a venue?" />
        <Select k="evening" opts={EVENINGS} label="Which showcase evening suits you?" />
        <input value={f.website} onChange={set('website')} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
        {err && <p className="text-red-700 font-bold">{err}</p>}
        <button disabled={busy} className={bigBtn} style={{ background: WYW_GREEN }}>{busy ? 'Sending…' : 'Enter free'}</button>
        <p className="text-xs text-[#767676]">Free entry, no purchase necessary. Both of you must attend a showcase evening and the Final Night. Off-peak date in 2027/28; partner prizes subject to availability.</p>
      </form>
    </Shell>
  );
}

// ── Couple books their slot ──
export function WywBookingPage({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await callWyw('couple', { token });
      setData(d);
      const open = d.evenings.filter((e) => !e.past);
      setDay((cur) => cur || d.evening_id || (open.find((e) => e.id === d.preferred_evening) || open[0] || d.evenings[0])?.id);
    } catch (x) { setErr(errorText(x)); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setBusy(true); setErr('');
    try { setBooked(await callWyw('book', { token, slot_at: slot })); }
    catch (x) { setErr(errorText(x)); setSlot(null); load(); }
    setBusy(false);
  };

  if (err && !data) return <Shell title="Book your show-around"><p className="text-base">{err}</p></Shell>;
  if (!data) return <Shell title="Book your show-around"><p>Loading…</p></Shell>;
  if (data.entered) return <Shell title="You're fully entered" sub={`Hi ${data.names}`}><p className="text-base">You've had your show-around and you're in the draw. See you on Saturday 20 February at 8pm. Bring the ticket from your email.</p></Shell>;
  if (booked) {
    return (
      <Shell title="You're booked in" sub={`Hi ${data.names}`}>
        <div className="bg-white rounded-xl p-5 text-lg font-bold" style={{ color: WYW_GREEN }}>{fmtSlot(booked.slot_at || slot)}</div>
        <p className="text-base mt-4">{booked.host || 'Our wedding team'} will meet you both at reception. We've emailed you a confirmation with a calendar invite.</p>
        <button onClick={() => { setBooked(null); setSlot(null); load(); }} className="mt-5 underline text-base" style={{ color: WYW_GREEN }}>Change time</button>
      </Shell>
    );
  }

  const ev = data.evenings.find((e) => e.id === day);
  return (
    <Shell title="Book your show-around" sub={`Hi ${data.names}. Pick a 15-minute slot with our wedding team. You both need to come.`}>
      {data.slot_at && (
        <div className="bg-white rounded-xl p-4 mb-4 text-base">Your current time: <b>{fmtSlot(data.slot_at)}</b>{data.host ? ` with ${data.host}` : ''}. Pick a new time below to change it.</div>
      )}
      <div className="text-xs font-bold tracking-wider text-[#5E5E5E] mb-2">1 · YOUR EVENING</div>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {data.evenings.map((e) => (
          <button key={e.id} disabled={e.past} onClick={() => { setDay(e.id); setSlot(null); }}
            className="h-16 rounded-xl border font-bold text-sm disabled:opacity-40"
            style={e.id === day ? { background: WYW_GREEN, color: '#fff', borderColor: WYW_GREEN } : { background: '#fff', color: WYW_GREEN, borderColor: '#D6D2C8' }}>
            {e.label}
          </button>
        ))}
      </div>
      <div className="text-xs font-bold tracking-wider text-[#5E5E5E] mb-2">2 · CHOOSE A TIME</div>
      <div className="grid grid-cols-4 gap-2">
        {(ev?.slots || []).map((s) => {
          const off = s.full || s.past;
          const on = s.slot_at === slot;
          const mine = s.slot_at === data.slot_at;
          return (
            <button key={s.slot_at} disabled={off} onClick={() => setSlot(s.slot_at)}
              className="h-11 rounded-lg border text-sm font-bold"
              style={on ? { background: WYW_GOLD, borderColor: WYW_GOLD } : off ? { background: '#ECE8E0', color: '#9A958C', borderColor: '#ECE8E0' } : { background: '#fff', borderColor: mine ? WYW_GREEN : '#D6D2C8' }}>
              {off ? 'Full' : fmtTime(s.slot_at)}
            </button>
          );
        })}
      </div>
      {err && <p className="text-red-700 font-bold mt-4">{err}</p>}
      <div className="mt-6">
        <div className="text-center font-bold mb-2" style={{ color: WYW_GREEN }}>{slot ? fmtSlot(slot) : 'Pick a time'}</div>
        <button disabled={!slot || busy} onClick={confirm} className={bigBtn} style={{ background: WYW_GREEN }}>{busy ? 'Booking…' : 'Confirm my slot'}</button>
        <p className="text-xs text-center text-[#5E5E5E] mt-2">You'll get a confirmation with a calendar invite and a link to change your time.</p>
      </div>
    </Shell>
  );
}

// ── Host's show-around list ──
export function WywTonightPage({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [evening, setEvening] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const run = useCallback(async (action = 'host', extra = {}) => {
    try { setData(await callWyw(action, { token, evening, ...extra })); setErr(''); }
    catch (x) { setErr(errorText(x)); }
  }, [token, evening]);

  useEffect(() => { run(); const t = setInterval(() => run(), 60000); return () => clearInterval(t); }, [run]);

  const act = async (action, id) => { setBusyId(id); await run(action, { couple_id: id }); setBusyId(null); };

  if (err && !data) return <Shell title="Show-arounds"><p className="text-base">{err}</p></Shell>;
  if (!data) return <Shell title="Show-arounds"><p>Loading…</p></Shell>;

  const done = data.couples.filter((c) => c.done).length;
  const next = data.couples.find((c) => !c.done);
  return (
    <Shell kicker={data.evening ? `${data.evening.label}${data.evening.today ? ' · TONIGHT' : ''}` : 'SHOW-AROUNDS'}
      title={`${data.host}'s couples`} sub={data.couples.length ? `${done} of ${data.couples.length} done` : 'Nobody booked with you yet'}>
      <div className="flex gap-2 mb-4">
        {data.evenings.map((e) => (
          <button key={e.id} onClick={() => setEvening(e.id)} className="flex-1 h-10 rounded-full border text-sm font-bold"
            style={e.id === data.evening?.id ? { background: WYW_GOLD, borderColor: WYW_GOLD, color: WYW_GREEN } : { background: '#fff', borderColor: '#D6D2C8' }}>
            {e.label}
          </button>
        ))}
      </div>
      <div className="space-y-2.5">
        {data.couples.map((c) => (
          <div key={c.id} className="flex items-center gap-3 min-h-[72px] px-3.5 py-2.5 rounded-xl border-2"
            style={{ background: c.done ? '#E5F4E4' : '#fff', borderColor: next?.id === c.id ? WYW_GREEN : c.done ? '#E5F4E4' : '#E6E2D9' }}>
            <span className="w-16 shrink-0 text-[17px] font-bold" style={{ color: WYW_GREEN }}>{c.time}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-[17px] font-bold">{c.names}</span>
              {c.phone && <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="text-sm text-[#5E5E5E]">{c.phone}</a>}
            </span>
            {c.done ? (
              <span className="shrink-0 flex flex-col items-end gap-1">
                <span className="text-base font-bold text-[#1E5A2B]">✓ Entered</span>
                {!c.emailed && (
                  <button disabled={busyId === c.id} onClick={() => act('undo', c.id)} className="h-8 px-3 rounded-md border border-[#9FB3A8] bg-white text-sm font-bold text-[#3B5A4A]">Undo</button>
                )}
              </span>
            ) : (
              <button disabled={busyId === c.id} onClick={() => act('done', c.id)} className="shrink-0 h-[50px] px-5 rounded-xl text-white text-[17px] font-bold" style={{ background: WYW_GREEN }}>Done</button>
            )}
          </div>
        ))}
      </div>
      {err && <p className="text-red-700 font-bold mt-4">{err}</p>}
      <p className="text-[15px] text-[#5E5E5E] text-center mt-6">Tap Done once both of them have had the tour. Their "fully entered" email goes 2 minutes later, so you can still Undo.</p>
    </Shell>
  );
}
